import { Inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import {
	DeviceConditionSeverity,
	InventoryDevice,
	LicenseSystemCommunicationState,
	SortedDeviceConditionCollection
} from '@shure/cloud/shared/models/devices';
import { APP_ENVIRONMENT, AppEnvironment } from '@shure/cloud/shared/utils/config';
import { DeviceCompatibilityLevel, DeviceStatus } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { PLACEHOLDER_VALUE } from '@shure/shared/models';

@Injectable()
export class DeviceStatusService {
	public static readonly lowBatteryThreshold = 5; // 5%
	public static readonly veryLowBatteryThreshold = 2; // 2%

	private logger: ILogger;

	constructor(
		logger: ILogger,
		private translocoService: TranslocoService,
		@Inject(APP_ENVIRONMENT) private appEnv: AppEnvironment
	) {
		this.logger = logger.createScopedLogger('DeviceStatusService');
	}

	public conditionsFor(device: InventoryDevice): SortedDeviceConditionCollection {
		const conditions = new SortedDeviceConditionCollection();
		if (device.status !== DeviceStatus.Online) {
			return conditions;
		}

		this.populateCompatibilityConditions(conditions, device);
		this.populateBatteryLevelConditions(conditions, device);

		if (this.appEnv.cdmFeatureFlags?.licenseV3) {
			this.populateLicenseConditions(conditions, device);
		}

		return conditions;
	}

	private populateCompatibilityConditions(
		conditions: SortedDeviceConditionCollection,
		device: InventoryDevice
	): void {
		if (device.compatibility === DeviceCompatibilityLevel.IncompatibleTooOld) {
			conditions.add({
				severity: DeviceConditionSeverity.High,
				description: this.translocoService.translate(
					'cloud.device-management.status.device-firmware-incompatible'
				)
			});
		}
	}

	private populateBatteryLevelConditions(conditions: SortedDeviceConditionCollection, device: InventoryDevice): void {
		if (!device.features.batteryLevel?.isMissing) {
			const batteryLevel = device.features.batteryLevel?.percentage ?? Number.NaN;
			if (batteryLevel <= DeviceStatusService.veryLowBatteryThreshold) {
				conditions.add({
					severity: DeviceConditionSeverity.High,
					description: this.translocoService.translate(
						'cloud.device-management.status.mic-battery-very-low',
						{
							percent: this.formatBatteryLevel(batteryLevel)
						}
					)
				});
			} else if (batteryLevel <= DeviceStatusService.lowBatteryThreshold) {
				conditions.add({
					severity: DeviceConditionSeverity.Moderate,
					description: this.translocoService.translate('cloud.device-management.status.mic-battery-low', {
						percent: this.formatBatteryLevel(batteryLevel)
					})
				});
			}
		}
	}

	private formatBatteryLevel(batteryLevel: number): string {
		return Number.isNaN(batteryLevel) ? PLACEHOLDER_VALUE : `${batteryLevel}%`;
	}

	private populateLicenseConditions(conditions: SortedDeviceConditionCollection, device: InventoryDevice): void {
		if (!device.features.licenseV3?.license || device.features.licenseV3.isMissing) {
			return;
		}

		if (
			device.features.licenseV3.license?.systemStatus.communicationState ===
			LicenseSystemCommunicationState.Failed
		) {
			conditions.add({
				severity: DeviceConditionSeverity.High,
				description: this.translocoService.translate('cloud.device-management.status.license-activation-failed')
			});
		}

		if (device.features.licenseV3.license?.systemStatus.ownershipMatch === false) {
			conditions.add({
				severity: DeviceConditionSeverity.High,
				description: this.translocoService.translate(
					'cloud.device-management.status.license-ownership-mismatch'
				)
			});
		}
	}
}
